/* eslint-disable max-statements */

import { Divider, Group, LoadingOverlay, Tabs } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { TransitGroup, TransitStatus } from '@e-origin/shared';

import { TableNotificationIndicator } from '@components';
import { ContentLayout } from '@features/layout';
import { useTransit } from '@hooks/transit';
import { countCustomsStateNotifications } from '../helpers';
import { TransitViewCustoms } from './TransitViewCustoms';
import { TransitViewDocuments } from './TransitViewDocuments';
import { TransitViewGeneralInfo } from './TransitViewGeneralInfo';
import { TransitViewHeader } from './TransitViewHeader';
import { TransitViewHouse } from './TransitViewHouse';
import { TransitViewIncidentsCustoms } from './TransitViewIncidentsCustoms';
import { TransitViewStakeholders } from './TransitViewStakeholders';

const customsStateNotificationIcon = (transit): React.ReactNode => {
  const { notificationsPending } = countCustomsStateNotifications(transit);

  return <TableNotificationIndicator notificationsPending={notificationsPending} />;
};

export const TransitView: React.FC = () => {
  const navigate = useNavigate();
  const { id, tabId } = useParams<{ id: string; tabId?: string }>() as any;

  const basePath = `/transits/${id}`;

  const [editDisabled, setEditDisabled] = useState<boolean>(true);
  const [editDisabledConditionalForArrival, setEditDisabledConditionalForArrival] = useState(false);
  const { data: transit, isLoading } = useTransit(id);
  const isArrival = transit?.generalInfo?.group === TransitGroup.ARRIVAL;

  const activeTab = useMemo(() => tabId || 'general-info', [tabId]);

  useEffect(() => {
    if (transit) {
      const isEditable = [TransitStatus.REJECTED, TransitStatus.NOT_SENT].includes(transit.generalInfo?.status);
      setEditDisabledConditionalForArrival(isArrival ? transit.generalInfo.status !== TransitStatus.UNLOAD_PERMISSION : !isEditable);
      setEditDisabled(!isEditable);
    }
  }, [transit]);

  useEffect(() => {
    if (!tabId) {
      navigate(`${basePath}/general-info`, { replace: true });
    }
  }, [tabId, navigate]);

  const handleTabChange = (newTab: string) => {
    navigate(`${basePath}/${newTab}`);
  };

  if (!transit) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      {/* todo: Check why it works without ContentLayout.Header */}
      <TransitViewHeader transit={transit} />

      <Divider></Divider>

      <ContentLayout.Content>
        <Tabs h="100%" value={activeTab} onChange={handleTabChange} orientation="vertical" px={50} keepMounted={false} py="lg">
          <Tabs.List>
            <Tabs.Tab value="general-info" data-pw="general-info-tab">
              General info
            </Tabs.Tab>
            <Tabs.Tab value="stakeholders" data-pw="stakeholders-tab">
              Stakeholders
            </Tabs.Tab>
            <Tabs.Tab value="documents" data-pw="documents-tab">
              Documents
            </Tabs.Tab>
            <Tabs.Tab value="house-consignments" data-pw="house-consignments-tab">
              House Consignments
            </Tabs.Tab>
            <Tabs.Tab value="incidents">Incidents</Tabs.Tab>
            <Tabs.Tab value="customs-state" data-pw="customs-state-tab">
              <Group wrap="nowrap">Customs State {customsStateNotificationIcon(transit)}</Group>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general-info">
            <TransitViewGeneralInfo transit={transit} editDisabled={editDisabled} editDisabledConditionalForArrival={editDisabledConditionalForArrival} />
          </Tabs.Panel>
          <Tabs.Panel value="stakeholders">
            <TransitViewStakeholders transit={transit} editDisabled={editDisabled} />
          </Tabs.Panel>
          <Tabs.Panel value="documents">
            <TransitViewDocuments transit={transit} editDisabled={editDisabled} editDisabledConditionalForArrival={editDisabledConditionalForArrival} />
          </Tabs.Panel>
          <Tabs.Panel value="house-consignments">
            <TransitViewHouse transit={transit} editDisabled={editDisabled} editDisabledConditionalForArrival={editDisabledConditionalForArrival} />
          </Tabs.Panel>
          <Tabs.Panel value="incidents">
            <TransitViewIncidentsCustoms transit={transit} />
          </Tabs.Panel>
          <Tabs.Panel value="customs-state">
            <TransitViewCustoms transit={transit} />
          </Tabs.Panel>
        </Tabs>
      </ContentLayout.Content>
    </ContentLayout>
  );
};
